import { apiFetch } from "./client";

export type JobMode = "one_shot" | "loop";
export type JobStatus = "pending" | "running" | "paused" | "completed" | "cancelled" | "error";

export interface JobSummary {
  id: string;
  name: string;
  script_id: string;
  phone_id: string;
  script_name: string | null;
  phone_name: string | null;
  status: JobStatus;
  mode: JobMode;
  max_runs: number | null;
  current_run_count: number;
  params: Record<string, unknown>;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
  success_count: number;
  failed_count: number;
}

export interface JobDetailResponse {
  success: boolean;
  job: JobSummary & { script_content: string };
  runs: RunSummary[];
  stats: Partial<Record<RunStatus, number>>;
}

export type RunStatus = "pending" | "dispatched" | "running" | "success" | "failed" | "cancelled" | "paused";

export interface RunSummary {
  id: string;
  job_id?: string;
  phone_id?: string;
  status: RunStatus;
  started_at: string | null;
  ended_at: string | null;
  dispatched_at: string | null;
  created_at: string;
  duration_ms: number | null;
  result_summary: string | null;
  failure_reason: string | null;
  failure_detail: string | null;
  account_id: string | null;
  paused_at: string | null;
  paused_step_path: string | null;
  paused_failure_id: string | null;
  job_name?: string;
  phone_name?: string;
}

interface ListResponse {
  success: boolean;
  jobs: JobSummary[];
  pagination: { total: number; limit: number; offset: number };
}

export interface JobsFilter {
  status?: JobStatus;
  phone_id?: string;
  script_id?: string;
  limit?: number;
  offset?: number;
}

function qs(filter: JobsFilter): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export async function listJobs(filter: JobsFilter = {}): Promise<{ jobs: JobSummary[]; total: number; }> {
  const data = await apiFetch<ListResponse>(`/api/jobs${qs(filter)}`);
  return { jobs: data.jobs, total: data.pagination.total };
}

export async function getJob(id: string): Promise<JobDetailResponse> {
  return apiFetch<JobDetailResponse>(`/api/jobs/${id}`);
}

export async function createJob(payload: {
  name: string;
  script_id: string;
  phone_id: string;
  mode?: JobMode;
  max_runs?: number | null;
  params?: Record<string, unknown>;
}): Promise<JobSummary> {
  const data = await apiFetch<{ success: boolean; job: JobSummary }>(`/api/jobs`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.job;
}

export async function pauseJob(id: string): Promise<void> {
  await apiFetch(`/api/jobs/${id}/pause`, { method: "POST" });
}

export async function resumeJob(id: string): Promise<void> {
  await apiFetch(`/api/jobs/${id}/resume`, { method: "POST" });
}

export async function stopJob(id: string): Promise<void> {
  await apiFetch(`/api/jobs/${id}/stop`, { method: "POST" });
}
